// Google Docs, Slides, Sheets, Forms and Drawings links sold as access links.
//
// WHY. A teacher selling a Google Slides resource pastes the link that Google's
// Share button copies, which ends in /edit. Delivered as it is, that link opens
// the teacher's own file for every buyer: one shared original that each buyer
// has to copy by hand (File > Make a copy), and that any buyer can change or
// empty for all the others if the file allows editing. Google's answer is the
// "make a copy" link, the same address ending in /copy, which asks each buyer
// to save their own copy to their Drive. It is how Google resources are sold on
// Teachers Pay Teachers, so the teachers moving across expect it.
//
// WHAT. The editors point out a Google link that does not give each buyer a
// copy, and switch it to the /copy link in one click (googleLinkAdvice, drawn by
// GoogleCopyLinkHint). Nothing rewrites a link on its own, at save or at
// delivery: a seller can mean a view-only link (Google refuses a copy of a file
// whose sharing stops viewers copying), and buyers must get the link the seller
// saved and tested. The buyer's download dialog names each kind for what it
// does (googleBuyerNote).
//
// Pure: no React, no Firestore.

import { isValidUrl } from './url-validation';

/** The Google editor a link belongs to, by the path segment Google uses for it. */
export type GoogleEditorApp = 'document' | 'presentation' | 'spreadsheets' | 'forms' | 'drawings';

/**
 * What a Google editor link gives a buyer:
 * - `copy`: their own copy (a /copy link, or a template preview's "Use template");
 * - `original`: the seller's own file (/edit, /view, /preview, a bare /d/<id>);
 * - `fill-in`: a form to answer, not the form itself (viewform, formResponse);
 * - `published`: the read-only page that Publish to web makes.
 */
export type GoogleLinkKind = 'copy' | 'original' | 'fill-in' | 'published';

export type GoogleEditorLink = {
  app: GoogleEditorApp;
  kind: GoogleLinkKind;
  /**
   * The file's "make a copy" link, or null when the link does not carry the
   * file's own id: a published page, and a published form's fill-in link, name a
   * separate published id (`/d/e/<id>`) that Google does not copy from.
   */
  copyUrl: string | null;
};

const APP_NAMES: Record<GoogleEditorApp, string> = {
  document: 'Google Docs',
  presentation: 'Google Slides',
  spreadsheets: 'Google Sheets',
  forms: 'Google Forms',
  drawings: 'Google Drawings',
};

/**
 * `/<app>/d/<id>/<action>`, optionally under a Workspace domain (`/a/<domain>/`)
 * and an account number (`/u/0/`). An `e/` before the id marks a published id.
 * Ids are long; a short segment is not taken for one (live-preview-link.ts uses
 * the same floor).
 */
const EDITOR_PATH = /^\/(?:a\/[^/]+\/)?(document|presentation|spreadsheets|forms|drawings)\/(?:u\/\d+\/)?d\/(e\/)?([A-Za-z0-9_-]{20,})(?:\/([^?#]*))?$/;

/** The product name of a Google editor: "Google Slides". */
export function googleAppName(app: GoogleEditorApp): string {
  return APP_NAMES[app];
}

/** A Google editor link, read; null for any other link, Drive file and folder links included. */
export function googleEditorLink(raw: string): GoogleEditorLink | null {
  const text = (raw ?? '').trim();
  if (!isValidUrl(text)) return null;
  const url = new URL(text);
  if (url.hostname.toLowerCase() !== 'docs.google.com') return null;
  const match = url.pathname.match(EDITOR_PATH);
  if (!match) return null;
  const app = match[1] as GoogleEditorApp;
  const published = match[2] === 'e/';
  const action = (match[4] ?? '').split('/')[0].toLowerCase();
  const copyUrl = published ? null : `https://docs.google.com/${app}/d/${match[3]}/copy`;
  if (app === 'forms' && (published || action === 'viewform' || action === 'formresponse')) {
    return { app, kind: 'fill-in', copyUrl };
  }
  if (published) return { app, kind: 'published', copyUrl: null };
  if (action === 'copy' || action === 'template') return { app, kind: 'copy', copyUrl };
  return { app, kind: 'original', copyUrl };
}

/** The editors' button that switches a link to its "make a copy" link. */
export const GOOGLE_COPY_FIX_LABEL = 'Use a "make a copy" link';

/** What the editors say under a Google link, and the link to switch to when there is one. */
export type GoogleLinkAdvice = {
  /** 'note' for a link that already gives each buyer a copy, 'warn' for one that does not. */
  tone: 'note' | 'warn';
  text: string;
  /** The "make a copy" link to switch to, or null when the seller has to fetch another link from Google. */
  fixUrl: string | null;
};

/** The editors' line under an access link, or null when it is not a Google editor link. */
export function googleLinkAdvice(raw: string): GoogleLinkAdvice | null {
  const link = googleEditorLink(raw);
  if (!link) return null;
  const app = googleAppName(link.app);
  switch (link.kind) {
  case 'copy':
    return {
      tone: 'note',
      fixUrl: null,
      text: `Buyers each save their own copy. In ${app}, set Share > General access to "Anyone with the link", or the copy won't open for them.`,
    };
  case 'original':
    return {
      tone: 'warn',
      fixUrl: link.copyUrl,
      text: `This opens your original ${app} file, which every buyer would share (and could change, if it allows editing). A "make a copy" link gives each buyer their own copy instead.`,
    };
  case 'fill-in':
    return link.copyUrl
      ? {
        tone: 'warn',
        fixUrl: link.copyUrl,
        text: 'This is your form\'s fill-in link, so buyers could only answer your form. A "make a copy" link gives each buyer their own copy of the form instead.',
      }
      : {
        tone: 'warn',
        fixUrl: null,
        text: 'This is your form\'s fill-in link, so buyers could only answer your form. To sell the form itself, paste the link from the form\'s editor (it ends in /edit).',
      };
  case 'published':
    return {
      tone: 'warn',
      fixUrl: null,
      text: `This is the file's published web page, so buyers can only read it. To give each buyer their own copy, paste the link from Share in ${app} instead.`,
    };
  }
}

/**
 * What the buyer's download dialog says under a Google link and on its button,
 * or null for any other link (which keeps the plain "Open link").
 */
export function googleBuyerNote(raw: string): { note: string; makesCopy: boolean } | null {
  const link = googleEditorLink(raw);
  if (!link) return null;
  const app = googleAppName(link.app);
  if (link.kind === 'copy') {
    return { makesCopy: true, note: `Opens ${app} and asks you to make your own copy in your Google Drive.` };
  }
  if (link.kind === 'original') {
    return { makesCopy: false, note: `Opens the seller's ${app} file. To keep your own copy, choose File > Make a copy.` };
  }
  return null;
}
